import { useEffect, useRef, useState } from 'react'
import {
  AvatarGroup,
  Box,
  Avatar,
  Tooltip,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { MoreVert, Edit, Delete, ContentCopy, AddReaction, Login, Logout, Settings } from '@mui/icons-material'
import { ChatMessageDto, MessageReaction } from '@api/types/chat'
import { chatApi } from '@api/endpoints/chat'
import { useConversationMessages, useChatStore } from '@store/chatStore'
import { useAuthStore } from '@store/authStore'
import { format, isToday, isYesterday, isSameDay } from 'date-fns'

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥']

interface TypingUser {
  userId: string
  userName: string
}

interface MessageThreadProps {
  conversationId: string
  onLoadMore?: () => void
  onEditMessage?: (messageId: string) => void
  onDeleteMessage?: (messageId: string) => void
  typingUsers?: TypingUser[]
}

export function MessageThread({
  conversationId,
  onLoadMore,
  onEditMessage,
  onDeleteMessage,
  typingUsers = [],
}: MessageThreadProps) {
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState<null | HTMLElement>(null)
  const [reactionTargetMessage, setReactionTargetMessage] = useState<ChatMessageDto | null>(null)
  const theme = useTheme()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { user } = useAuthStore()
  const messageState = useConversationMessages(conversationId)
  const messages = messageState.items || []
  const loading = messageState.loading
  const hasMore = messageState.hasMore
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedMessage, setSelectedMessage] = useState<ChatMessageDto | null>(null)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const prevMessagesLengthRef = useRef(0)

  // Scroll to bottom on initial load (instant) and new messages (smooth)
  useEffect(() => {
    if (messages.length > 0) {
      const isInitialLoad = !initialLoadDone
      const isNewMessage = messages.length > prevMessagesLengthRef.current

      if (isInitialLoad) {
        // Wait for DOM to fully paint before scrolling on initial load
        // Double rAF ensures layout is complete (especially for Collapse animations)
        setInitialLoadDone(true)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight
            }
          })
        })
      } else if (isNewMessage) {
        // Smooth scroll for new messages
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }

      prevMessagesLengthRef.current = messages.length
    }
  }, [messages.length, initialLoadDone])

  // Reset initial load flag when conversation changes
  useEffect(() => {
    setInitialLoadDone(false)
    prevMessagesLengthRef.current = 0
  }, [conversationId])

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget
    if (scrollTop === 0 && hasMore && !loading && onLoadMore) {
      onLoadMore()
    }
  }

  const handleMessageMenuOpen = (event: React.MouseEvent<HTMLElement>, message: ChatMessageDto) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedMessage(message)
  }

  const handleMessageMenuClose = () => {
    setMenuAnchor(null)
    setSelectedMessage(null)
  }

  const handleCopyMessage = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.content)
    }
    handleMessageMenuClose()
  }

  const handleEditMessage = () => {
    if (selectedMessage && onEditMessage) {
      onEditMessage(selectedMessage.id)
    }
    handleMessageMenuClose()
  }

  const handleDeleteMessage = () => {
    if (selectedMessage && onDeleteMessage) {
      onDeleteMessage(selectedMessage.id)
    }
    handleMessageMenuClose()
  }

  const handleReaction = async (emoji: string, message: ChatMessageDto) => {
    if (!user) return
    handleMessageMenuClose()
    setReactionPickerAnchor(null)
    setReactionTargetMessage(null)

    // Check if user already reacted with this exact emoji - toggle off
    const currentReactions = message.reactions || []
    const existingSameEmoji = currentReactions.find(
      (r) => r.userId === user.userId && r.emoji === emoji
    )
    // Check if user has a different reaction on this message
    const existingOtherEmoji = currentReactions.find(
      (r) => r.userId === user.userId && r.emoji !== emoji
    )
    const sendEmoji = existingSameEmoji ? null : emoji

    // Optimistic update
    const updateMessage = useChatStore.getState().updateMessage
    if (updateMessage) {
      let newReactions = currentReactions
      // Remove the user's previous different reaction
      if (existingOtherEmoji) {
        newReactions = newReactions.filter(
          (r) => !(r.userId === user.userId && r.emoji === existingOtherEmoji.emoji)
        )
      }
      if (existingSameEmoji) {
        // Toggling off the same emoji
        newReactions = newReactions.filter((r) => !(r.userId === user.userId && r.emoji === emoji))
      } else {
        // Adding new emoji
        newReactions = [...newReactions, { userId: user.userId, displayName: user.displayName || user.userName || '', emoji }]
      }
      updateMessage(conversationId, message.id, { reactions: newReactions })
    }

    try {
      await chatApi.reactToMessage(conversationId, message.id, sendEmoji)
    } catch (error) {
      console.error('Failed to react to message:', error)
    }
  }

  const handleReactionPickerOpen = (event: React.MouseEvent<HTMLElement>, message: ChatMessageDto) => {
    setReactionPickerAnchor(event.currentTarget)
    setReactionTargetMessage(message)
  }

  const handleReactionPickerClose = () => {
    setReactionPickerAnchor(null)
    setReactionTargetMessage(null)
  }

  const formatMessageDate = (date: Date) => {
    if (isToday(date)) return 'Today'
    if (isYesterday(date)) return 'Yesterday'
    return format(date, 'MMMM d, yyyy')
  }

  const renderDateDivider = (date: Date) => (
    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
      <Divider sx={{ flex: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
        {formatMessageDate(date)}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  )

  // Compute which message is the "last read" for each user (read receipt positions)
  // For each user in readBy arrays, find the latest message they've read
  const readReceiptsByMessageId = (() => {
    const userLastRead = new Map<string, { messageId: string; displayName: string; messageIndex: number }>()

    messages.forEach((msg, idx) => {
      if (!msg.readBy) return
      msg.readBy.forEach((rb) => {
        // Skip the current user's own read receipts
        if (rb.userId === user?.userId) return
        // Skip the message sender seeing their own messages
        if (rb.userId === msg.senderId) return
        const existing = userLastRead.get(rb.userId)
        if (!existing || idx > existing.messageIndex) {
          userLastRead.set(rb.userId, { messageId: msg.id, displayName: rb.displayName, messageIndex: idx })
        }
      })
    })

    // Group by messageId
    const result = new Map<string, { userId: string; displayName: string }[]>()
    userLastRead.forEach((val, userId) => {
      const list = result.get(val.messageId) || []
      list.push({ userId, displayName: val.displayName })
      result.set(val.messageId, list)
    })
    return result
  })()

  // Group reactions by emoji for a message
  const groupReactions = (reactions: MessageReaction[]) => {
    const grouped = new Map<string, string[]>()
    reactions.forEach((r) => {
      const list = grouped.get(r.emoji) || []
      list.push(r.displayName)
      grouped.set(r.emoji, list)
    })
    return grouped
  }

  const parseMetadata = (metadata: string | null | undefined) => {
    if (!metadata) return null
    try {
      return JSON.parse(metadata) as {
        Action?: string
        UserId?: string
        DisplayName?: string
        Changes?: Record<string, string>
      }
    } catch {
      return null
    }
  }

  const getSystemMessageIcon = (action: string | undefined) => {
    switch (action) {
      case 'participant_joined':
        return <Login sx={{ fontSize: '0.875rem' }} />
      case 'participant_left':
        return <Logout sx={{ fontSize: '0.875rem' }} />
      case 'settings_changed':
        return <Settings sx={{ fontSize: '0.875rem' }} />
      default:
        return null
    }
  }

  const getSystemMessageColor = (action: string | undefined) => {
    switch (action) {
      case 'participant_joined':
        return theme.palette.success.main
      case 'participant_left':
        return theme.palette.warning.main
      case 'settings_changed':
        return theme.palette.info.main
      default:
        return theme.palette.text.secondary
    }
  }

  const renderSystemMessage = (message: ChatMessageDto, index: number) => {
    const timestamp = message.timestamp.endsWith('Z') ? message.timestamp : message.timestamp + 'Z'
    const messageDate = new Date(timestamp)
    const prevTimestamp = index > 0
      ? (messages[index - 1].timestamp.endsWith('Z') ? messages[index - 1].timestamp : messages[index - 1].timestamp + 'Z')
      : null
    const showDateDivider =
      index === 0 ||
      !isSameDay(messageDate, new Date(prevTimestamp!))

    const meta = parseMetadata(message.metadata)
    const icon = getSystemMessageIcon(meta?.Action)
    const color = getSystemMessageColor(meta?.Action)

    return (
      <Box key={message.id}>
        {showDateDivider && renderDateDivider(messageDate)}
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              color,
              bgcolor: alpha(color, 0.08),
              px: 2,
              py: 0.5,
              borderRadius: '12px',
            }}
          >
            {icon}
            <Typography
              variant="caption"
              sx={{ color: 'inherit', fontStyle: 'italic' }}
            >
              {message.content}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: alpha(color, 0.5) }}
            >
              &middot; {format(messageDate, 'h:mm a')}
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderMessage = (message: ChatMessageDto, index: number) => {
    if (message.isSystemMessage) {
      return renderSystemMessage(message, index)
    }

    const isOwnMessage = message.senderId === user?.userId
    // Append 'Z' to indicate UTC if not already present
    const timestamp = message.timestamp.endsWith('Z') ? message.timestamp : message.timestamp + 'Z'
    const messageDate = new Date(timestamp)
    const prevTimestamp = index > 0
      ? (messages[index - 1].timestamp.endsWith('Z') ? messages[index - 1].timestamp : messages[index - 1].timestamp + 'Z')
      : null
    const showDateDivider =
      index === 0 ||
      !isSameDay(messageDate, new Date(prevTimestamp!))

    // Check if we can edit (within 5 minutes)
    const canEdit =
      isOwnMessage &&
      Date.now() - messageDate.getTime() < 5 * 60 * 1000 &&
      !message.isDeleted

    // Also fix the edit check in menu - use messageDate

    return (
      <Box key={message.id}>
        {showDateDivider && renderDateDivider(messageDate)}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isOwnMessage ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            mb: 1.5,
            gap: 1,
          }}
        >
          {!isOwnMessage && (
            <Avatar
              src={message.senderAvatarUrl || undefined}
              sx={{ width: 32, height: 32, mt: 0.5 }}
            >
              {message.senderName?.charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Box
            sx={{
              maxWidth: '70%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
            }}
          >
            {!isOwnMessage && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                {message.senderName}
              </Typography>
            )}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                '&:hover .message-menu-button': {
                  opacity: 1,
                },
              }}
            >
              <Box
                sx={{
                  ...(isOwnMessage
                    ? {
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: 'primary.contrastText',
                      }
                    : {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                      }),
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  boxShadow: 1,
                  ...(message.isDeleted && {
                    opacity: 0.5,
                    fontStyle: 'italic',
                  }),
                }}
              >
                {/* Display image if present */}
                {message.imageUrl && !message.isDeleted && (
                  <Box sx={{ mb: message.content ? 1 : 0 }}>
                    <img
                      src={message.imageUrl}
                      alt="Chat image"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                      }}
                      onClick={() => window.open(message.imageUrl!, '_blank')}
                    />
                  </Box>
                )}
                {(message.content || message.isDeleted) && (
                  <Typography variant="body2">
                    {message.isDeleted ? 'This message was deleted' : message.content}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.5,
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isOwnMessage
                        ? alpha(theme.palette.primary.contrastText, 0.7)
                        : 'text.secondary',
                    }}
                  >
                    {format(messageDate, 'h:mm a')}
                  </Typography>
                  {message.isEdited && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isOwnMessage
                          ? alpha(theme.palette.primary.contrastText, 0.7)
                          : 'text.secondary',
                      }}
                    >
                      (edited)
                    </Typography>
                  )}
                </Box>
              </Box>
              {!message.isDeleted && (
                <IconButton
                  size="small"
                  className="message-menu-button"
                  onClick={(e) => handleMessageMenuOpen(e, message)}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    p: 0.5,
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Emoji reactions */}
            {message.reactions && message.reactions.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mt: -0.5,
                  alignSelf: isOwnMessage ? 'flex-end' : 'flex-start',
                }}
              >
                {Array.from(groupReactions(message.reactions).entries()).map(([emoji, users]) => {
                  const userReacted = message.reactions?.some(
                    (r) => r.userId === user?.userId && r.emoji === emoji
                  )
                  return (
                    <Tooltip
                      key={emoji}
                      title={users.join(', ')}
                      arrow
                    >
                      <Box
                        onClick={() => handleReaction(emoji, message)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.25,
                          bgcolor: userReacted
                            ? alpha(theme.palette.primary.main, 0.12)
                            : 'background.paper',
                          border: 1,
                          borderColor: userReacted ? 'primary.main' : 'divider',
                          borderRadius: '12px',
                          px: 0.75,
                          py: 0.25,
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          boxShadow: 1,
                          transition: 'all 0.15s',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <span style={{ fontSize: '0.875rem' }}>{emoji}</span>
                        {users.length > 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                            {users.length}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  )
                })}
                {/* Add reaction button inline */}
                <Box
                  onClick={(e) => handleReactionPickerOpen(e, message)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '12px',
                    px: 0.75,
                    py: 0.25,
                    cursor: 'pointer',
                    boxShadow: 1,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <AddReaction sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                </Box>
              </Box>
            )}

            {/* Read receipt avatars */}
            {readReceiptsByMessageId.has(message.id) && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  mt: 0.25,
                }}
              >
                <AvatarGroup
                  max={5}
                  sx={{
                    '& .MuiAvatar-root': {
                      width: 16,
                      height: 16,
                      fontSize: '0.5rem',
                      border: `1.5px solid ${theme.palette.background.default}`,
                    },
                  }}
                >
                  {readReceiptsByMessageId.get(message.id)!.map((reader) => (
                    <Tooltip key={reader.userId} title={`Seen by ${reader.displayName}`} arrow>
                      <Avatar sx={{ width: 16, height: 16 }}>
                        {reader.displayName.charAt(0).toUpperCase()}
                      </Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  }

  if (messages.length === 0 && !loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1">No messages yet</Typography>
        <Typography variant="body2">Start the conversation!</Typography>
      </Box>
    )
  }

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflow: 'auto',
        p: 2,
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Loading indicator for older messages */}
      {loading && hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Messages */}
      {messages.map((message, index) => renderMessage(message, index))}

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, ml: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {typingUsers.length === 1
              ? `${typingUsers[0].userName} is typing...`
              : `${typingUsers.map(u => u.userName).join(', ')} are typing...`}
          </Typography>
        </Box>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Message context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMessageMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Quick reaction row */}
        {selectedMessage && !selectedMessage.isDeleted && (
          <Box sx={{ display: 'flex', gap: 0.5, px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
            {QUICK_REACTIONS.map((emoji) => {
              const alreadyReacted = selectedMessage.reactions?.some(
                (r) => r.userId === user?.userId && r.emoji === emoji
              )
              return (
                <Box
                  key={emoji}
                  onClick={() => handleReaction(emoji, selectedMessage)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.25rem',
                    lineHeight: 1,
                    p: 0.5,
                    borderRadius: 1,
                    bgcolor: alreadyReacted ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      transform: 'scale(1.2)',
                    },
                    transition: 'all 0.15s',
                  }}
                >
                  {emoji}
                </Box>
              )
            })}
          </Box>
        )}
        <MenuItem onClick={handleCopyMessage}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy</ListItemText>
        </MenuItem>
        {selectedMessage?.senderId === user?.userId && (
          <>
            {Date.now() - new Date(selectedMessage.timestamp.endsWith('Z') ? selectedMessage.timestamp : selectedMessage.timestamp + 'Z').getTime() < 5 * 60 * 1000 && (
              <MenuItem onClick={handleEditMessage}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleDeleteMessage}>
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Standalone reaction picker (from + button on existing reactions) */}
      <Menu
        anchorEl={reactionPickerAnchor}
        open={Boolean(reactionPickerAnchor)}
        onClose={handleReactionPickerClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5 }}>
          {QUICK_REACTIONS.map((emoji) => {
            const alreadyReacted = reactionTargetMessage?.reactions?.some(
              (r) => r.userId === user?.userId && r.emoji === emoji
            )
            return (
              <Box
                key={emoji}
                onClick={() => reactionTargetMessage && handleReaction(emoji, reactionTargetMessage)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  p: 0.5,
                  borderRadius: 1,
                  bgcolor: alreadyReacted ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'scale(1.2)',
                  },
                  transition: 'all 0.15s',
                }}
              >
                {emoji}
              </Box>
            )
          })}
        </Box>
      </Menu>
    </Box>
  )
}
